import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

import { QuickInterval } from '@/api/models/parking/lib/parking.types'

import { AppCheckboxBadged } from '../../../checkbox'
import { getRangeFromQuickInterval } from '../../utils/getRangeFromQuickInterval'
import { DateRange } from '../Calendar.types'

type Props = {
  customQuickFilters: QuickInterval[]
  range?: DateRange
  setRange: (range?: DateRange) => void
  calendarType?: 'calendar' | 'hours'
  onFilterClick?: () => void
}

export const CalendarCustomQuickFilters = ({
  customQuickFilters,
  range = { from: undefined, to: undefined },
  setRange,
  onFilterClick,
  calendarType = 'calendar'
}: Props) => {
  const [localRange, setLocalRange] = useState(range)
  const compareUnit = calendarType === 'hours' ? 'minute' : 'day'

  useEffect(() => {
    setLocalRange(range)
  }, [range])

  const hasSyncedDefaultRef = useRef(false)

  useEffect(() => {
    if (hasSyncedDefaultRef.current) {
      return
    }

    const defaultFilter = customQuickFilters.find(filter => filter.isDefault)

    if (!defaultFilter) {
      return
    }

    hasSyncedDefaultRef.current = true

    const isEmptyRange = !range?.from || !range?.to
    // на старте period ещё не пришёл с бэка и равен заглушке "сегодня-сегодня",
    // её нужно заменить реальным дефолтным фильтром один раз при загрузке
    const isStubRange =
      Boolean(range?.from && range?.to) &&
      dayjs(range.from).isSame(dayjs(), compareUnit) &&
      dayjs(range.to).isSame(dayjs(), compareUnit)

    if (!isEmptyRange && !isStubRange) {
      return
    }

    const selectedRange = getRangeFromQuickInterval(defaultFilter)

    setRange(selectedRange)
    setLocalRange(selectedRange)
  }, [compareUnit, customQuickFilters, range, setRange])

  const isFilterActive = (filter: QuickInterval) => {
    if (!localRange?.from || !localRange?.to) {
      return false
    }

    const selectedRange = getRangeFromQuickInterval(filter)

    return (
      dayjs(localRange.from).isSame(dayjs(selectedRange.from), compareUnit) &&
      dayjs(localRange.to).isSame(dayjs(selectedRange.to), compareUnit)
    )
  }

  const onQuickFilterClick = (filter: QuickInterval) => {
    const selectedRange = getRangeFromQuickInterval(filter)
    if (onFilterClick) {
      onFilterClick()
    }

    setRange(selectedRange)
    setLocalRange(selectedRange)
  }

  return (
    <div className="mt-8 flex flex-wrap gap-8">
      {customQuickFilters.map(filter => (
        <AppCheckboxBadged
          key={`${filter.name}-${filter.startDateTime}-${filter.endDateTime}`}
          checked={isFilterActive(filter)}
          onChange={event => {
            event.preventDefault()
            onQuickFilterClick(filter)
          }}
          label={filter.name}
          closable={false}
        />
      ))}
    </div>
  )
}
